import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ModeEditOutlineOutlined,
  DeleteOutlineOutlined,
  ScheduleSendOutlined,
  MoreVertOutlined,
  AssignmentOutlined,
  PlayArrowOutlined,
  QueueMusicOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import '../playlist.css';
import { tr } from "../../../i18n";

const CustomTableCell = styled(TableCell)(() => ({
  color: '#374151',
  fontSize: '10px',
  fontWeight: '600',
  fontFamily: "'Electrolize', sans-serif",
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  padding: '8px 10px',
  borderBottom: '2px solid #0d9488',
  backgroundColor: '#fafbfc',
}));

const BodyCell = styled(TableCell)(() => ({
  color: '#111827',
  fontSize: '12px',
  fontWeight: '400',
  fontFamily: "'Electrolize', sans-serif",
  lineHeight: 1.35,
  padding: '9px 10px',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle',
}));

const PlaylistListView = ({
  playlists,
  onEdit,
  onDelete,
  onSchedule,
  onSelect,
  onAssignToScreens,
  onPlay,
  selectedPlaylist,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  isLoading = false,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPlaylist, setMenuPlaylist] = useState(null);

  const handleMenuOpen = (event, playlist) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuPlaylist(playlist);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlaylist(null);
  };

  const menuAction = (fn) => {
    if (menuPlaylist && fn) fn(menuPlaylist);
    handleMenuClose();
  };

  const contentCount = (p) => (p.content ? p.content.length : 0);

  const screenCount = (p) => (p.mac_list ? p.mac_list.length : 0);

  return (
    <>
      <div className="playlist-list-card">
        <Paper
          elevation={0}
          className="playlist-list-paper"
          sx={{ minWidth: 0, overflow: 'hidden' }}
        >
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflowX: 'auto', width: '100%' }}>
          <Table
            stickyHeader
            size="small"
            className="playlist-data-table"
            sx={{ width: "100%", minWidth: { xs: 280, md: 720 }, tableLayout: { md: "auto" } }}
          >
            <TableHead>
              <TableRow className="playlist-table-header-row">
                <CustomTableCell align="center" sx={{ width: 50 }}>
                  #
                </CustomTableCell>
                <CustomTableCell align="left">{tr("Name")}</CustomTableCell>
                <CustomTableCell align="center">{tr("Items")}</CustomTableCell>
                <CustomTableCell align="center">{tr("Screens")}</CustomTableCell>
                <CustomTableCell align="center">{tr("Schedule")}</CustomTableCell>
                <CustomTableCell align="center">{tr("Created")}</CustomTableCell>
                <CustomTableCell align="center">{tr("Actions")}</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {playlists.length > 0 ? (
                playlists.map((playlist, index) => (
                  <TableRow
                    key={playlist._id}
                    onClick={() => navigate(`/playlist/${playlist._id}`)}
                    className={`playlist-table-body-row ${selectedPlaylist?._id === playlist._id ? 'playlist-table-body-row--selected' : ''}`}
                  >
                    <BodyCell align="center">
                      {index + 1}
                    </BodyCell>
                    <BodyCell align="left">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box className="playlist-list-thumb">
                          <QueueMusicOutlined className="playlist-list-thumb-icon" />
                        </Box>
                        <span className="playlist-list-name">
                          {playlist.name}
                        </span>
                      </Box>
                    </BodyCell>
                    <BodyCell align="center">
                      <span
                        className={`playlist-list-items-badge ${
                          contentCount(playlist) === 0 ? 'playlist-list-items-badge--empty' : ''
                        }`}
                      >
                        {contentCount(playlist)}
                      </span>
                    </BodyCell>
                    <BodyCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <span
                          className={`playlist-list-screen-count ${
                            screenCount(playlist) === 0 ? 'playlist-list-screen-count--zero' : ''
                          }`}
                        >
                          {screenCount(playlist)}
                        </span>
                        {screenCount(playlist) > 1 && (
                          <span className="playlist-list-shared-badge">{tr("Shared")}</span>
                        )}
                      </Box>
                    </BodyCell>
                    <BodyCell align="center">
                      {playlist.schedule?.scheduled ? (
                        <span className="playlist-list-scheduled">{tr("Scheduled")}</span>
                      ) : (
                        <span className="playlist-list-none">{tr("None")}</span>
                      )}
                    </BodyCell>
                    <BodyCell align="center">
                      {playlist.createdAt
                        ? new Date(playlist.createdAt).toLocaleDateString()
                        : '—'}
                    </BodyCell>
                    <BodyCell align="center">
                      <div className="playlist-list-actions">
                        {!readOnly ? (
                          <>
                            <ModeEditOutlineOutlined
                              className="playlist-list-icon playlist-list-icon--edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(playlist);
                              }}
                            />
                            <ScheduleSendOutlined
                              className="playlist-list-icon playlist-list-icon--schedule"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSchedule(playlist);
                              }}
                            />
                            <DeleteOutlineOutlined
                              className="playlist-list-icon playlist-list-icon--delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(playlist);
                              }}
                            />
                            <MoreVertOutlined
                              className="playlist-list-icon playlist-list-icon--more"
                              onClick={(e) => handleMenuOpen(e, playlist)}
                            />
                          </>
                        ) : (
                          <PlayArrowOutlined
                            className="playlist-list-icon playlist-list-icon--more"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onPlay) onPlay(playlist);
                            }}
                          />
                        )}
                      </div>
                    </BodyCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <BodyCell colSpan={7} align="center" className="playlist-list-empty">
                    {tr("No playlists found.")}
                  </BodyCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 20, 50]}
            count={pagination.totalItems || 0}
            rowsPerPage={pagination.itemsPerPage || 10}
            page={(pagination.currentPage || 1) - 1}
            onPageChange={(e, newPage) => onPageChange(newPage + 1)}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              borderTop: '1px solid #E5E7EB',
              fontFamily: "'Electrolize', sans-serif",
              color: '#111827',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                fontSize: '13px',
                fontFamily: "'Electrolize', sans-serif",
                color: '#111827',
              },
            }}
          />
        )}
        </Paper>
      </div>

      {!readOnly && (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            fontFamily: "'Electrolize', sans-serif",
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => menuAction(onPlay)}
          sx={{ fontSize: '13px', fontFamily: "'Electrolize', sans-serif", gap: 1 }}
        >
          <ListItemIcon>
            <PlayArrowOutlined sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '13px' }}>
            {tr("Play")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuAction(onAssignToScreens)}
          sx={{ fontSize: '13px', fontFamily: "'Electrolize', sans-serif", gap: 1 }}
        >
          <ListItemIcon>
            <AssignmentOutlined sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '13px' }}>
            {tr("Assign to Screens (shared)")}
          </ListItemText>
        </MenuItem>
      </Menu>
      )}
    </>
  );
};

export default PlaylistListView;
